<script setup lang="ts">
const { isLoggedIn } = useAuth()

onMounted(() => {
  if (!isLoggedIn.value) navigateTo('/account/login')
})

const { data: rawAddresses, pending, refresh } = useFetch('/api/customer/addresses', { server: false })
const addresses = computed(() => (rawAddresses.value as any[]) ?? [])

const emptyForm = () => ({
  firstName: '', lastName: '', address1: '', address2: '',
  city: '', province: '', zip: '', country: 'Indonesia', phone: '',
})

const showForm = ref(false)
const editingId = ref<string | null>(null)
const form = reactive(emptyForm())
const submitting = ref(false)
const errorMsg = ref('')

function toBase64url(str: string) {
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function openAdd() {
  Object.assign(form, emptyForm())
  editingId.value = null
  errorMsg.value = ''
  showForm.value = true
}

function openEdit(addr: any) {
  Object.assign(form, {
    firstName: addr.firstName ?? '',
    lastName: addr.lastName ?? '',
    address1: addr.address1 ?? '',
    address2: addr.address2 ?? '',
    city: addr.city ?? '',
    province: addr.province ?? '',
    zip: addr.zip ?? '',
    country: addr.country ?? 'Indonesia',
    phone: addr.phone ?? '',
  })
  editingId.value = toBase64url(addr.id)
  errorMsg.value = ''
  showForm.value = true
}

function cancelForm() {
  showForm.value = false
  editingId.value = null
}

async function saveAddress() {
  errorMsg.value = ''
  submitting.value = true
  try {
    if (editingId.value) {
      await $fetch(`/api/customer/address/${editingId.value}`, { method: 'PUT', body: { ...form } })
    }
    else {
      await $fetch('/api/customer/address', { method: 'POST', body: { ...form } })
    }
    showForm.value = false
    editingId.value = null
    await refresh()
  }
  catch (err: any) {
    errorMsg.value = err?.data?.statusMessage ?? 'Something went wrong.'
  }
  finally {
    submitting.value = false
  }
}

async function removeAddress(addr: any) {
  if (!confirm('Remove this address?')) return
  try {
    await $fetch(`/api/customer/address/${toBase64url(addr.id)}`, { method: 'DELETE' })
    await refresh()
  }
  catch (err: any) {
    alert(err?.data?.statusMessage ?? 'Could not remove address.')
  }
}

async function setDefault(addr: any) {
  try {
    await $fetch('/api/customer/address/default', { method: 'PUT', body: { addressId: toBase64url(addr.id) } })
    await refresh()
  }
  catch (err: any) {
    alert(err?.data?.statusMessage ?? 'Could not update default.')
  }
}

const inputClass = 'bg-surface border border-stroke h-11 font-sans text-sm text-dark placeholder:text-muted px-3.5 rounded-lg outline-none focus:border-brand transition-colors w-full'
</script>

<template>
  <div class="bg-page flex flex-col items-start">
    <!-- Header -->
    <div class="flex flex-col gap-2 items-start pb-10 pt-14 px-4 md:px-20 w-full">
      <p class="font-sans text-[13px] text-muted">
        <NuxtLink to="/account" class="hover:text-secondary transition-colors">Account</NuxtLink>
        &nbsp;/&nbsp; Addresses
      </p>
      <p class="font-serif font-semibold text-4xl md:text-[42px] text-dark">Addresses</p>
    </div>

    <!-- Main -->
    <div class="flex flex-col md:flex-row gap-12 items-start pb-24 px-4 md:px-20 w-full">
      <!-- Sidebar -->
      <div class="flex md:flex-col gap-1 items-start shrink-0 w-full md:w-[220px] overflow-x-auto">
        <NuxtLink to="/account" class="flex items-center px-4 py-3 rounded-lg shrink-0 hover:bg-surface transition-colors">
          <p class="font-sans text-[15px] text-secondary whitespace-nowrap">Dashboard</p>
        </NuxtLink>
        <NuxtLink to="/account/orders" class="flex items-center px-4 py-3 rounded-lg shrink-0 hover:bg-surface transition-colors">
          <p class="font-sans text-[15px] text-secondary whitespace-nowrap">Order History</p>
        </NuxtLink>
        <div class="bg-surface flex items-center px-4 py-3 rounded-lg shrink-0">
          <p class="font-sans font-medium text-[15px] text-dark whitespace-nowrap">Addresses</p>
        </div>
        <NuxtLink to="/account/details" class="flex items-center px-4 py-3 rounded-lg shrink-0 hover:bg-surface transition-colors">
          <p class="font-sans text-[15px] text-secondary whitespace-nowrap">Account Details</p>
        </NuxtLink>
      </div>

      <!-- Content -->
      <div class="flex flex-1 flex-col gap-6 items-start min-w-0 w-full">
        <!-- Add button -->
        <div class="flex items-center justify-between w-full">
          <p class="font-serif font-semibold text-[22px] text-dark">Saved Addresses</p>
          <button
            class="bg-primary flex items-center gap-1.5 px-4 py-2.5 rounded-md font-label text-sm text-white"
            @click="openAdd"
          >+ Add New</button>
        </div>

        <!-- Loading -->
        <div v-if="pending" class="font-sans text-sm text-muted">Loading addresses…</div>

        <!-- Add / Edit form -->
        <div v-if="showForm" class="bg-surface border border-stroke flex flex-col gap-4 items-start p-6 rounded-xl w-full">
          <p class="font-serif font-semibold text-lg text-dark">{{ editingId ? 'Edit Address' : 'New Address' }}</p>
          <div class="flex flex-col md:flex-row gap-3 w-full">
            <div class="flex flex-col gap-1.5 flex-1">
              <label class="font-label font-light text-[13px] text-secondary">First Name</label>
              <input v-model="form.firstName" type="text" placeholder="Jane" :class="inputClass" />
            </div>
            <div class="flex flex-col gap-1.5 flex-1">
              <label class="font-label font-light text-[13px] text-secondary">Last Name</label>
              <input v-model="form.lastName" type="text" placeholder="Doe" :class="inputClass" />
            </div>
          </div>
          <div class="flex flex-col gap-1.5 w-full">
            <label class="font-label font-light text-[13px] text-secondary">Address Line 1</label>
            <input v-model="form.address1" type="text" placeholder="123 Main Street" :class="inputClass" />
          </div>
          <div class="flex flex-col gap-1.5 w-full">
            <label class="font-label font-light text-[13px] text-secondary">Address Line 2</label>
            <input v-model="form.address2" type="text" placeholder="Apt, Suite, etc." :class="inputClass" />
          </div>
          <div class="flex flex-col md:flex-row gap-3 w-full">
            <div class="flex flex-col gap-1.5 flex-1">
              <label class="font-label font-light text-[13px] text-secondary">City</label>
              <input v-model="form.city" type="text" placeholder="Bandung" :class="inputClass" />
            </div>
            <div class="flex flex-col gap-1.5 flex-1">
              <label class="font-label font-light text-[13px] text-secondary">Province / State</label>
              <input v-model="form.province" type="text" placeholder="Jawa Barat" :class="inputClass" />
            </div>
            <div class="flex flex-col gap-1.5 w-[120px] shrink-0">
              <label class="font-label font-light text-[13px] text-secondary">ZIP</label>
              <input v-model="form.zip" type="text" placeholder="40163" :class="inputClass" />
            </div>
          </div>
          <div class="flex flex-col md:flex-row gap-3 w-full">
            <div class="flex flex-col gap-1.5 flex-1">
              <label class="font-label font-light text-[13px] text-secondary">Country</label>
              <input v-model="form.country" type="text" placeholder="Indonesia" :class="inputClass" />
            </div>
            <div class="flex flex-col gap-1.5 flex-1">
              <label class="font-label font-light text-[13px] text-secondary">Phone</label>
              <input v-model="form.phone" type="tel" placeholder="+62 812 3456 7890" :class="inputClass" />
            </div>
          </div>
          <p v-if="errorMsg" class="font-sans text-[13px] text-error">{{ errorMsg }}</p>
          <div class="flex gap-3">
            <button
              class="bg-primary px-5 py-2.5 rounded-md font-label text-sm text-white disabled:opacity-60"
              :disabled="submitting"
              @click="saveAddress"
            >{{ submitting ? 'Saving…' : 'Save Address' }}</button>
            <button
              class="border border-stroke px-5 py-2.5 rounded-md font-label font-light text-sm text-secondary hover:border-brand transition-colors"
              @click="cancelForm"
            >Cancel</button>
          </div>
        </div>

        <!-- Address cards -->
        <div v-if="!pending && addresses.length === 0 && !showForm" class="font-sans text-sm text-muted">
          No saved addresses yet.
        </div>
        <div class="flex flex-col md:flex-row flex-wrap gap-5 w-full">
          <div
            v-for="addr in addresses"
            :key="addr.id"
            class="bg-surface flex flex-col gap-1.5 items-start p-5 rounded-xl w-full md:w-[calc(50%-10px)]"
          >
            <div class="flex items-center justify-between w-full">
              <p class="font-sans font-medium text-sm text-brand">
                {{ addr.isDefault ? 'Default Address' : 'Address' }}
              </p>
              <span
                v-if="addr.isDefault"
                class="bg-primary-subtle border border-brand/30 font-label font-light text-xs text-brand px-2 py-0.5 rounded-full"
              >Default</span>
            </div>
            <p class="font-sans text-sm text-dark">{{ addr.firstName }} {{ addr.lastName }}</p>
            <p class="font-sans text-[13px] text-secondary">{{ addr.address1 }}</p>
            <p v-if="addr.address2" class="font-sans text-[13px] text-secondary">{{ addr.address2 }}</p>
            <p class="font-sans text-[13px] text-secondary">{{ addr.city }}, {{ addr.province }} {{ addr.zip }}</p>
            <p class="font-sans text-[13px] text-secondary">{{ addr.country }}</p>
            <div class="h-1" />
            <div class="flex gap-3 items-center">
              <button class="font-sans text-xs text-brand hover:underline" @click="openEdit(addr)">Edit</button>
              <span class="text-muted text-xs">·</span>
              <button class="font-sans text-xs text-secondary hover:text-error transition-colors" @click="removeAddress(addr)">Remove</button>
              <template v-if="!addr.isDefault">
                <span class="text-muted text-xs">·</span>
                <button class="font-sans text-xs text-secondary hover:text-brand transition-colors" @click="setDefault(addr)">Set as Default</button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>
